/**
 * The replay run collection — separate from EVERY production collection,
 * with its own retention.
 *
 * `benchmark:replay-runs` lives in `addon-benchmark`'s own namespace, not
 * `pipeline-analytics`'s. That is not a cosmetic choice: `pipeline-analytics:
 * tracks` is the retention CASCADE ROOT (`docs/reference/storage.md`) — a row
 * that ends up there drags its events, media, faces, plates and CLIP vector
 * down with it when it ages out. A replay run has no place in that cascade,
 * and putting it in a different addon's collection (which the tracks sweep
 * never even NAMES, let alone walks) makes that true by construction, not by
 * discipline. `pruneBefore` here is this store's OWN clock — nothing else in
 * the system reads or writes `benchmark:replay-runs`.
 *
 * One row per run: the source, the FULL resolved plan (steps + settings +
 * zones), and the verdict — serialized whole. A summary would make a delta
 * between two runs from yesterday unreconstructable, which defeats the
 * comparison this whole feature exists for.
 */
import type { SettingsStoreClient } from '@camstack/types'

/**
 * @durable class=audit owner=benchmark
 *   write="one row per COMPLETED replay run, inserted once by `orchestrateReplayRun` when the operator replays a clip through the pipeline — the source, the full resolved plan (steps + settings + zones) and the verdict, serialized whole. Nothing rewrites a row: a run is a finished measurement, and re-running produces a new id."
 *   retention="`audit`, and not a `ledger`, because nothing in the system ever reads a row back to DECIDE anything — `list` feeds the benchmark page and the two-id diff a human asked for, and losing the table costs comparison history, never behaviour. It is deliberately in `benchmark:`, outside the `pipeline-analytics` cascade the tracks sweep walks. CAVEAT, measured not assumed: `pruneBefore` is this store's own clock and NOTHING CALLS IT outside the spec — no boot pass, no timer, no operator action. So today the bound is the operator's habit: one row per replay they run, forever. That is a retention gap, recorded here rather than papered over."
 */
export const REPLAY_RUNS_COLLECTION = 'benchmark:replay-runs'

export const REPLAY_RUNS_COLUMNS = [
  { name: 'id', type: 'TEXT' as const, primaryKey: true, notNull: true },
  { name: 'deviceId', type: 'INTEGER' as const, notNull: true },
  { name: 'nodeId', type: 'TEXT' as const, notNull: true },
  { name: 'clipId', type: 'TEXT' as const, notNull: true },
  { name: 'createdAtMs', type: 'INTEGER' as const, notNull: true },
  { name: 'source', type: 'JSON' as const, notNull: true },
  { name: 'plan', type: 'JSON' as const, notNull: true },
  { name: 'verdict', type: 'JSON' as const, notNull: true },
] as const

const REPLAY_RUNS_INDEXES = [
  { name: 'idx_replay_runs_device_created', columns: ['deviceId', 'createdAtMs'] as const },
  { name: 'idx_replay_runs_created', columns: ['createdAtMs'] as const },
] as const

export interface StoredReplayRun {
  readonly id: string
  readonly deviceId: number
  readonly nodeId: string
  readonly clipId: string
  readonly createdAtMs: number
  /** Serialized `ReplaySource` — kept opaque here; this store does not
   *  interpret it, only carries it. */
  readonly source: unknown
  /** Serialized `ReplayPlan` — steps, settings, zones, in FULL. */
  readonly plan: unknown
  /** Serialized `ReplayVerdict` — tracks, discarded, counters, complete. */
  readonly verdict: unknown
}

export class ReplayStore {
  constructor(private readonly store: SettingsStoreClient) {}

  /** Declare the collection. Call in `onInitialize`, before the first
   *  `save` — an undeclared collection crash-loops the runner. */
  static async declare(store: SettingsStoreClient): Promise<void> {
    await store.declareCollection.mutate({
      collection: REPLAY_RUNS_COLLECTION,
      columns: [...REPLAY_RUNS_COLUMNS],
      indexes: [...REPLAY_RUNS_INDEXES],
    })
  }

  async save(run: StoredReplayRun): Promise<void> {
    const { id, ...rest } = run
    // Cast through `unknown` at the SAME documented boundary
    // `OpsLogStore.append` crosses (`store/ops-log-store.ts:122`): a `record`
    // with known, readonly fields is not structurally a mutable
    // `Record<string, unknown>` index signature, and the settings-store cap
    // itself takes that generic shape because it does not know any
    // collection's row type.
    await this.store.insert.mutate({
      collection: REPLAY_RUNS_COLLECTION,
      record: { id, data: rest as unknown as Record<string, unknown> }, // TECH-DEBT: settings-store boundary, see the comment above.
    })
  }

  /** Runs newest-first, optionally scoped to one device. */
  async list(query: {
    readonly deviceId?: number
    readonly limit?: number
  }): Promise<readonly StoredReplayRun[]> {
    const filter: {
      where?: Record<string, unknown>
      orderBy: { field: string; direction: 'desc' }
      limit?: number
    } = { orderBy: { field: 'createdAtMs', direction: 'desc' } }
    if (query.deviceId !== undefined) filter.where = { deviceId: query.deviceId }
    if (query.limit !== undefined) filter.limit = query.limit
    const rows = await this.store.query.query({ collection: REPLAY_RUNS_COLLECTION, filter })
    // Same documented boundary as `save` above, in reverse: a query row's
    // `data` is a generic `Record<string, unknown>` — this store is the only
    // reader that knows it holds a `StoredReplayRun` shape.
    return rows.map((r) => ({ id: r.id, ...r.data }) as unknown as StoredReplayRun) // TECH-DEBT: settings-store boundary, see `save` above.
  }

  /** Delete every run created before `cutoffMs`. This store's OWN retention
   *  clock — see the file header for why that matters. Returns the count so
   *  a caller can log it, the way `OpsLogStore`-adjacent sweeps do. */
  async pruneBefore(cutoffMs: number): Promise<number> {
    const { deleted } = await this.store.deleteWhere.mutate({
      collection: REPLAY_RUNS_COLLECTION,
      filter: { whereBetween: { createdAtMs: [0, cutoffMs] } },
    })
    return deleted
  }
}
